/**
 * Templates - Generates HTML templates for voice interface
 * 
 * Required from parent (voiceInterface):
 * - config: { language, translations, ... }
 * - isMobile: boolean
 * - t(key): string (translation helper method)
 * - getText(directConfigKey, translationKey): string
 * - getTooltip(key): string
 * 
 * State owned by this module:
 * - None (stateless template generation)
 */

export class Templates {
  constructor(voiceInterface) {
    this.voiceInterface = voiceInterface;
    this.config = voiceInterface.config;
  }

  /**
   * Helper to access translation method from parent
   */
  t(key) {
    return this.voiceInterface.t(key);
  }

  /**
   * Helper to access getText method from parent
   */
  getText(directConfigKey, translationKey) {
    return this.voiceInterface.getText(directConfigKey, translationKey);
  }

  /**
   * Helper to access getTooltip method from parent
   */
  getTooltip(key) {
    return this.voiceInterface.getTooltip(key);
  }

  /**
   * Generate HTML for voice interface.
   * Automatically detects mobile vs desktop based on voiceInterface.isMobile
   * and returns the appropriate template.
   *
   * @returns {string} HTML template string for the voice interface
   */
  generateHTML() {
    // Mobile UI - minimized bar design
    if (this.voiceInterface.isMobile) {
      return this.generateMobileHTML();
    }
    
    // Desktop UI - original design
    return `<div class="voice-interface" id="voiceInterface">
      <!-- Before Call State -->
      <div id="voiceIdleState">
        <div class="voice-avatar" id="voiceAvatar">🤖</div>
        <div class="voice-status">
          <div class="voice-status-title">${this.getText('startCallTitle', 'clickToStartCall')}</div>
          <div class="voice-status-subtitle">${this.getText('startCallSubtitle', 'realTimeVoice')}</div>
        </div>
        <button class="start-call-btn" id="startCallBtn">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
          <span>${this.getText('startCallButtonText', 'startCall')}</span>
        </button>
      </div>
      <!-- During Call State - Desktop View -->
      <div id="voiceActiveState" style="display: none;">
        <!-- Original Multi-Row Voice Section (shown when history is collapsed) -->
        <div class="desktop-voice-section" id="desktopVoiceSection">
          <!-- Timer -->
          <div class="desktop-timer">
            <div class="timer-dot"></div>
            <span id="desktopTimerText">00:00</span>
          </div>

          <!-- Main Waveform -->
          <div class="desktop-main-waveform" id="desktopMainWaveform">
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
            <div class="waveform-bar"></div>
          </div>

          <!-- Status -->
          <div class="desktop-status" id="desktopStatus">
            <div class="status-dot"></div>
            <span id="desktopStatusText">${this.t('listening') || 'Listening...'}</span>
          </div>

          <!-- Controls -->
          <div class="desktop-controls">
            <button class="desktop-control-btn secondary" id="desktopMuteBtn" title="${this.getTooltip('mute')}">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="desktop-mic-icon">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
              </svg>
              <svg fill="none" viewBox="0 0 24 24" class="desktop-mic-off-icon" style="display: none; position: absolute;">
                <!-- Original mic icon (same as normal state) -->
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="2" fill="none"/>
                <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" stroke-width="2"/>
                <!-- Red circle surrounding the icon -->
                <circle cx="12" cy="12" r="11.8" fill="none" stroke="#ef4444" stroke-width="2"/>
                <!-- Red diagonal strike line -->
                <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round"/>
              </svg>
            </button>
            <button class="desktop-control-btn danger" id="desktopEndCallBtn" title="${this.getTooltip('endCall')}">
              <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" style="overflow: visible;">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" transform="rotate(135 12 12)"/>
              </svg>
            </button>
            <button class="desktop-control-btn secondary" id="desktopSpeakerBtn" title="${this.getTooltip('speaker')}">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="desktop-speaker-icon">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/>
              </svg>
              <svg fill="none" viewBox="0 0 24 24" class="desktop-speaker-off-icon" style="display: none; position: absolute;">
                <!-- Original speaker icon (same as normal state) -->
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" stroke-width="2" fill="none"/>
                <!-- Red circle surrounding the icon -->
                <circle cx="12" cy="12" r="11.8" fill="none" stroke="#ef4444" stroke-width="2"/>
                <!-- Red diagonal strike line -->
                <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Compact Voice Section - Single Row Layout (shown only when history is expanded) -->
        <div class="compact-voice-section" id="compactVoiceSection" style="display: none;">
          <!-- Left side: Avatar + Info -->
          <div class="compact-left">
            <!-- Avatar container with waveform -->
            <div class="compact-avatar" id="desktopAvatarCompact">
              <!-- Main Waveform - Scaled down for compact view -->
              <div class="desktop-main-waveform" id="desktopMainWaveformCompact">
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
                <div class="waveform-bar"></div>
              </div>
            </div>
            
            <!-- Timer and Status stacked vertically -->
            <div class="compact-info">
              <div class="compact-timer" id="desktopTimerCompact">
                <span class="timer-dot"></span>
                <span id="compactTimerText">00:00</span>
              </div>
              <div class="compact-status" id="desktopStatusCompact">
                <span class="status-dot"></span>
                <span id="compactStatusText">${this.t('listening') || 'Listening'}</span>
              </div>
            </div>
          </div>
          
          <!-- Right side: Control buttons (reuse same IDs, handlers will work for both) -->
          <div class="compact-controls">
            <button class="compact-control-btn secondary" id="desktopMuteBtnCompact" title="${this.getTooltip('mute')}">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="desktop-mic-icon">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                <line x1="12" y1="19" x2="12" y2="23"/>
              </svg>
              <svg fill="none" viewBox="0 0 24 24" class="desktop-mic-off-icon" style="display: none; position: absolute;">
                <!-- Original mic icon (same as normal state) -->
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" stroke="currentColor" stroke-width="2" fill="none"/>
                <line x1="12" y1="19" x2="12" y2="23" stroke="currentColor" stroke-width="2"/>
                <!-- Red circle surrounding the icon -->
                <circle cx="12" cy="12" r="11.8" fill="none" stroke="#ef4444" stroke-width="2"/>
                <!-- Red diagonal strike line -->
                <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round"/>
              </svg>
            </button>
            <button class="compact-control-btn danger" id="desktopEndCallBtnCompact" title="${this.getTooltip('endCall')}">
              <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24" style="overflow: visible;">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" transform="rotate(135 12 12)"/>
              </svg>
            </button>
            <button class="compact-control-btn secondary" id="desktopSpeakerBtnCompact" title="${this.getTooltip('speaker')}">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="desktop-speaker-icon">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/>
              </svg>
              <svg fill="none" viewBox="0 0 24 24" class="desktop-speaker-off-icon" style="display: none; position: absolute;">
                <!-- Original speaker icon (same as normal state) -->
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M19.07 4.93a10 10 0 010 14.14" stroke="currentColor" stroke-width="2" fill="none"/>
                <path d="M15.54 8.46a5 5 0 010 7.07" stroke="currentColor" stroke-width="2" fill="none"/>
                <!-- Red circle surrounding the icon -->
                <circle cx="12" cy="12" r="11.8" fill="none" stroke="#ef4444" stroke-width="2"/>
                <!-- Red diagonal strike line -->
                <line x1="4" y1="4" x2="20" y2="20" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Conversation Panel -->
        <div class="desktop-conversation-panel collapsed" id="desktopConversationPanel">
          <!-- Header -->
          <div class="conversation-header" id="conversationHeader">
            <span class="conversation-label">${this.t('conversation') || 'Conversation'}</span>
            <span class="conversation-toggle">
              <span id="conversationToggleText">Show history</span>
              <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </span>
          </div>

          <!-- Collapsed: Live transcript only (2 lines) -->
          <div class="live-transcript-collapsed" id="liveTranscriptCollapsed">
            <div class="live-indicator">
              <div class="live-dot"></div>
              <span>Live</span>
            </div>
            <div class="live-text-collapsed muted" id="liveTextCollapsed">
              ${this.t('transcriptWillAppear') || 'Transcript will appear here...'}<span class="typing-cursor"></span>
            </div>
          </div>

          <!-- Expanded: Full conversation history -->
          <div class="conversation-history" id="conversationHistory">
            <div class="conversation-messages" id="conversationMessages">
              <!-- Messages will be rendered here -->
            </div>
            
            <!-- Live message at bottom -->
            <div class="live-message-row" id="liveMessageRow" style="display: none;">
              <div class="message-avatar assistant">
                <div class="orb"></div>
              </div>
              <div class="message-bubble">
                <span class="live-badge">Live</span>
                <span id="liveMessageText"></span><span class="typing-cursor"></span>
              </div>
            </div>
          </div>

          <!-- Text Input -->
          <div class="voice-text-input-area">
            <div class="voice-input-wrapper">
              <input type="text" class="voice-text-input" id="voiceTextInput" placeholder="${this.getText('inputPlaceholder', 'typeMessage') || 'Type a message...'}">
              <button class="voice-send-btn" id="voiceSendBtn">
                <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            </div>
            <div class="voice-input-hint" id="voiceInputHint">
              <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              </svg>
              ${this.getText('voiceActiveHint', 'voiceActive') || 'Voice is still active • Type or speak'}
            </div>
          </div>
        </div>
      </div>
    </div>`;
  }

  /**
   * Generate mobile-specific HTML for minimized voice call UI
   */
  generateMobileHTML() {
    // For mobile, idle state goes in widget, but active state will be appended to body
    return `<div class="voice-interface mobile-voice-interface" id="voiceInterface">
      <!-- Before Call State (same as desktop) -->
      <div id="voiceIdleState">
        <div class="voice-avatar" id="voiceAvatar">🤖</div>
        <div class="voice-status">
          <div class="voice-status-title">${this.getText('startCallTitle', 'clickToStartCall')}</div>
          <div class="voice-status-subtitle">${this.getText('startCallSubtitle', 'realTimeVoice')}</div>
        </div>
        <button class="start-call-btn" id="startCallBtn">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
          </svg>
          <span>${this.getText('startCallButtonText', 'startCall')}</span>
        </button>
      </div>
      
      <!-- Mobile Active Call State - Hidden placeholder (actual bar will be appended to body) -->
      <div id="voiceActiveState" class="mobile-active-state" style="display: none;">
        <!-- Duration Badge -->
        <div class="mobile-duration-badge" id="mobileDurationBadge">
          <div class="mobile-duration-dot"></div>
          <span id="mobileDurationText">00:00</span>
        </div>
        
        <!-- Main Bar -->
        <div class="mobile-voice-bar">
          <!-- Top Row: Status, Waveform, Controls -->
          <div class="mobile-top-row">
            <!-- Status Indicator -->
            <div class="mobile-status-indicator">
              <div class="mobile-status-dot" id="mobileStatusDot"></div>
              <span class="mobile-status-text" id="mobileStatusText">Listening...</span>
            </div>
            
            <!-- Centered Waveform -->
            <div class="mobile-waveform-center" id="mobileWaveform">
              ${Array(8).fill(0).map((_, i) => 
                `<div class="mobile-waveform-bar" data-index="${i}"></div>`
              ).join('')}
            </div>
            
            <!-- Controls -->
            <div class="mobile-controls">
              <button class="mobile-control-btn" id="mobileMuteBtn" aria-label="Mute microphone">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-icon">
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-mic-off-icon" style="display: none;">
                  <line x1="2" x2="22" y1="2" y2="22"/>
                  <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/>
                  <path d="M5 10v2a7 7 0 0 0 12 5"/>
                  <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12"/>
                  <line x1="12" x2="12" y1="19" y2="22"/>
                </svg>
              </button>
              
              <button class="mobile-control-btn" id="mobileSpeakerBtn" aria-label="Toggle speaker">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-icon">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                </svg>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="mobile-speaker-off-icon" style="display: none;">
                  <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                  <line x1="22" x2="16" y1="9" y2="15"/>
                  <line x1="16" x2="22" y1="9" y2="15"/>
                </svg>
              </button>
              
              <button class="mobile-end-call-btn" id="mobileEndCallBtn" aria-label="End call">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                  <line x1="22" x2="2" y1="2" y2="22"/>
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Bottom Row: Transcript (expandable) -->
          <button class="mobile-transcript-row" id="mobileTranscriptRow">
            <p class="mobile-transcript-text" id="mobileTranscriptText">${this.t('transcriptWillAppear')}</p>
            <div class="mobile-transcript-footer">
              <span class="mobile-expand-hint">tap to expand & type</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.6;">
                <path d="M7 17L17 7M17 7H7M17 7V17"/>
              </svg>
            </div>
          </button>
        </div>
      </div>
      
      <!-- Expanded Transcript Overlay -->
      <div class="mobile-transcript-overlay" id="mobileTranscriptOverlay" style="display: none;">
        <div class="mobile-expanded-transcript" id="mobileExpandedTranscript">
          <!-- Header -->
          <div class="mobile-transcript-header">
            <div class="mobile-header-left">
              <div class="mobile-header-status">
                <div class="mobile-header-status-dot"></div>
                <span class="mobile-header-status-text" id="mobileHeaderStatusText">Listening...</span>
                <span class="mobile-header-duration" id="mobileHeaderDuration">00:00</span>
              </div>
              <span class="mobile-transcript-label">CONVERSATION</span>
            </div>
            <button class="mobile-close-transcript-btn" id="mobileCloseTranscriptBtn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          
          <!-- Messages Container -->
          <div class="mobile-messages-container" id="mobileMessagesContainer">
            <!-- Messages will be dynamically added here -->
          </div>
          
          <!-- Text Input Area -->
          <div class="mobile-input-area">
            <div class="mobile-input-wrapper">
              <input
                type="text"
                placeholder="Type a message..."
                id="mobileTextInput"
                class="mobile-text-input"
              />
              <button class="mobile-send-btn" id="mobileSendBtn" disabled>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                </svg>
              </button>
            </div>
            <p class="mobile-input-hint">🎙️ Voice is still active • Type or speak</p>
          </div>
        </div>
      </div>
    </div>`;
  }
}
